import React, { useState, useEffect, useCallback } from 'react';

import { EditOutlined, InfoCircleOutlined, GlobalOutlined, PlusOutlined, DeleteOutlined } from '@ant-design/icons';
import FloatingActionBar, { FloatingActionBarSpacer } from '@components/shared/ui/FloatingActionBar';
import ListPageHeader from '@components/shared/ListPageHeader';
import Can from '@shared/permissions/Can';
import WarehouseForm from '@components/shared/warehouses/WarehouseForm';
import { Card, Form, Input, Button, Space, message, Spin, Descriptions, Tooltip, Radio, Typography, Modal, Select, Table, Popconfirm } from 'antd';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router-dom';

import { operatorApiFetch } from '@services/operator/apiService';
import { warehouseZoneService } from '@services/operator/warehouseZoneService';

const WarehouseShow = () => {
  const { t } = useTranslation();
  const { id } = useParams();
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [warehouse, setWarehouse] = useState(null);
  const [editMode, setEditMode] = useState(false);

  // Shelf Location Template State
  const [locationTemplate, setLocationTemplate] = useState('');

  // Zone Management State
  const [zones, setZones] = useState([]);
  const [zonesLoading, setZonesLoading] = useState(false);
  const [zoneModalVisible, setZoneModalVisible] = useState(false);
  const [zoneModalLoading, setZoneModalLoading] = useState(false);
  const [editingZone, setEditingZone] = useState(null);
  const [zoneForm] = Form.useForm();

  // Delete Zone with Shelves State
  const [deleteZoneModal, setDeleteZoneModal] = useState({ visible: false, zone: null, shelvesCount: 0 });
  const [deleteAction, setDeleteAction] = useState('replace');
  const [replacementZoneId, setReplacementZoneId] = useState(null);
  const [deleteZoneLoading, setDeleteZoneLoading] = useState(false);

  useEffect(() => {
    fetchWarehouse();
  }, [id]);

  const fetchWarehouse = async () => {
    try {
      setLoading(true);
      const response = await operatorApiFetch(`warehouses/${id}`);
      const data = await response.json();

      if (data.success && data.data) {
        setWarehouse(data.data);
        setLocationTemplate(data.data.default_shelf_location_template || '');
        form.setFieldsValue({
          name: data.data.name,
          address: data.data.address,
          postal_code: data.data.postal_code,
          country_code: data.data.country_code,
          state: data.data.state,
          city: data.data.city,
          district: data.data.district,
          phone: data.data.phone,
          allow_mixed_inventory_types: data.data.allow_mixed_inventory_types,
          storage_mode: data.data.storage_mode,
          timezone: data.data.timezone,
        });
      } else {
        throw new Error('Failed to fetch warehouse');
      }
    } catch (error) {
      console.error('Failed to fetch warehouse:', error);
      message.error(t('common.error.fetch_failed'));
    } finally {
      setLoading(false);
    }
  };

  const submitWarehouse = async (values) => {
    try {
      setSaving(true);

      const response = await operatorApiFetch(`warehouses/${id}`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ warehouse: values }),
      });

      const data = await response.json();

      if (data.success) {
        message.success(t('common.message.update_success'));
        setWarehouse({ ...warehouse, ...data.data });
        setLocationTemplate(data.data.default_shelf_location_template || '');
        setEditMode(false);
      } else if (data.message === 'warehouse_mode_locked') {
        // Race case: another tab/browser created an operational record between
        // form open and submit. Re-fetch so the UI rewrites itself to locked state.
        message.error(t('common.error.warehouse_mode_locked'));
        fetchWarehouse();
      } else {
        message.error(data.message || t('common.error.update_failed'));
      }
    } catch (error) {
      console.error('Failed to update warehouse:', error);
      message.error(t('common.error.update_failed'));
    } finally {
      setSaving(false);
    }
  };

  const handleCancel = () => {
    form.setFieldsValue({
      name: warehouse.name,
      address: warehouse.address,
      postal_code: warehouse.postal_code,
      country_code: warehouse.country_code,
      state: warehouse.state,
      city: warehouse.city,
      district: warehouse.district,
      phone: warehouse.phone,
      allow_mixed_inventory_types: warehouse.allow_mixed_inventory_types,
      storage_mode: warehouse.storage_mode,
      timezone: warehouse.timezone,
    });
    setLocationTemplate(warehouse.default_shelf_location_template || '');
    setEditMode(false);
  };

  // === Zone Management ===
  const fetchZones = useCallback(async () => {
    if (!id) return;
    try {
      setZonesLoading(true);
      const data = await warehouseZoneService.getRecords({ warehouse_id: id, page_size: 100 });
      if (data.success) {
        setZones(data.data || []);
      }
    } catch (error) {
      console.error('Failed to fetch zones:', error);
    } finally {
      setZonesLoading(false);
    }
  }, [id]);

  useEffect(() => {
    if (id) fetchZones();
  }, [id, fetchZones]);

  const handleZoneAdd = () => {
    setEditingZone(null);
    zoneForm.resetFields();
    setZoneModalVisible(true);
  };

  const handleZoneEdit = (zone) => {
    setEditingZone(zone);
    zoneForm.setFieldsValue({ name: zone.name });
    setZoneModalVisible(true);
  };

  const handleZoneModalOk = async () => {
    try {
      const values = await zoneForm.validateFields();
      setZoneModalLoading(true);

      let data;
      if (editingZone) {
        data = await warehouseZoneService.update(editingZone.id, { name: values.name });
      } else {
        data = await warehouseZoneService.create({ name: values.name, warehouse_id: id });
      }

      if (data.success) {
        message.success(t(editingZone ? 'common.message.update_success' : 'common.message.create_success'));
        setZoneModalVisible(false);
        fetchZones();
      } else {
        message.error(data.message ? t(`common.error.${data.message}`, data.message) : t('common.error.operation_failed'));
      }
    } catch (error) {
      // validation failed, ignore
    } finally {
      setZoneModalLoading(false);
    }
  };

  const handleZoneDelete = async (zone) => {
    try {
      const data = await warehouseZoneService.delete(zone.id);
      if (data.success) {
        message.success(t('common.message.delete_success'));
        fetchZones();
      } else if (data.message === 'warehouse_zone_has_shelves') {
        setDeleteZoneModal({
          visible: true,
          zone,
          shelvesCount: data.interpolations?.shelves_count || 0,
        });
        setDeleteAction('replace');
        setReplacementZoneId(null);
      } else {
        message.error(data.message ? t(`common.error.${data.message}`, data.message) : t('common.error.operation_failed'));
      }
    } catch (error) {
      console.error('Failed to delete zone:', error);
      message.error(t('common.error.operation_failed'));
    }
  };

  const handleDeleteZoneConfirm = async () => {
    const { zone } = deleteZoneModal;
    if (!zone) return;

    if (deleteAction === 'replace' && !replacementZoneId) {
      message.warning(t('shared.warehouses.zones.select_replacement_zone'));
      return;
    }

    try {
      setDeleteZoneLoading(true);
      const params = deleteAction === 'replace'
        ? { replacement_zone_id: replacementZoneId }
        : { clear_zone: true };

      const data = await warehouseZoneService.delete(zone.id, params);
      if (data.success) {
        message.success(t('common.message.delete_success'));
        setDeleteZoneModal({ visible: false, zone: null, shelvesCount: 0 });
        fetchZones();
      } else {
        message.error(data.message ? t(`common.error.${data.message}`, data.message) : t('common.error.operation_failed'));
      }
    } catch (error) {
      console.error('Failed to delete zone:', error);
      message.error(t('common.error.operation_failed'));
    } finally {
      setDeleteZoneLoading(false);
    }
  };

  const canEditZones = warehouse?.editable === true;

  const zoneColumns = [
    {
      title: t('shared.warehouses.zones.name_label'),
      dataIndex: 'name',
      key: 'name',
    },
    ...(canEditZones
      ? [{
          title: t('shared.action'),
          key: 'action',
          width: 160,
          render: (_, record) => (
            <Space>
              <Button size="small" onClick={() => handleZoneEdit(record)}>
                {t('common.button.edit')}
              </Button>
              <Popconfirm
                title={t('shared.warehouses.zones.delete_confirm')}
                onConfirm={() => handleZoneDelete(record)}
                okText={t('common.button.confirm')}
                cancelText={t('common.button.cancel')}
              >
                <Button size="small" danger icon={<DeleteOutlined />}>
                  {t('common.button.delete')}
                </Button>
              </Popconfirm>
            </Space>
          ),
        }]
      : []),
  ];

  if (loading || !warehouse) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '400px' }}>
        <Spin size="large" />
      </div>
    );
  }

  return (
    <div style={{ padding: '24px' }}>
      <ListPageHeader
        items={['warehouse_settings', 'warehouse_settings.warehouses', { label: warehouse.name }]}
        actions={
          warehouse.editable && !editMode ? (
            <Can requiredPermission="warehouses:write">
              <Button
                type="primary"
                icon={<EditOutlined />}
                onClick={() => setEditMode(true)}
              >
                {t('common.button.edit')}
              </Button>
            </Can>
          ) : undefined
        }
      />
      <div>
        {editMode ? (
          <>
            <WarehouseForm
              form={form}
              lockStorageMode={warehouse.fresh !== true}
              locationTemplate={locationTemplate}
              onLocationTemplateChange={setLocationTemplate}
              onFinish={submitWarehouse}
            />
            <FloatingActionBarSpacer />
          </>
        ) : (
          <Descriptions bordered column={2}>
            <Descriptions.Item label={t('models.warehouse.name')}>
              {warehouse.name}
            </Descriptions.Item>
            <Descriptions.Item label={t('models.warehouse.phone')}>
              {warehouse.phone || '-'}
            </Descriptions.Item>
            <Descriptions.Item label={t('models.warehouse.full_address')} span={2}>
              {warehouse.full_address || '-'}
            </Descriptions.Item>
            {warehouse.storage_mode === 'dynamic' && (
              <Descriptions.Item label={
                <Space>
                  {t('models.warehouse.allow_mixed_inventory_types')}
                  <Tooltip title={t('models.warehouse.allow_mixed_inventory_types_tooltip')}>
                    <InfoCircleOutlined style={{ color: 'rgba(0,0,0,0.45)' }} />
                  </Tooltip>
                </Space>
              }>
                {warehouse.allow_mixed_inventory_types ? t('common.yes') : t('common.no')}
              </Descriptions.Item>
            )}
            <Descriptions.Item label={
              <Space>
                <GlobalOutlined />
                {t('models.warehouse.timezone')}
              </Space>
            }>
              {warehouse.timezone || '-'}
            </Descriptions.Item>
            <Descriptions.Item label={t('operator.organization_settings.storage_mode')}>
              {t(`operator.organization_settings.storage_mode_${warehouse.storage_mode}`)}
            </Descriptions.Item>
            <Descriptions.Item label={t('models.warehouse.default_shelf_location_template')}>
              <Typography.Text code>
                {warehouse.default_shelf_location_template || t('shared.shelves.batch_new.template_modes.standard') + ' ({row}-{column}-{layer})'}
              </Typography.Text>
            </Descriptions.Item>
            <Descriptions.Item label={t('common.created_at')}>
              {warehouse.created_at ? new Date(warehouse.created_at).toLocaleString() : '-'}
            </Descriptions.Item>
            <Descriptions.Item label={t('common.updated_at')}>
              {warehouse.updated_at ? new Date(warehouse.updated_at).toLocaleString() : '-'}
            </Descriptions.Item>
          </Descriptions>
        )}
      </div>

      {editMode && (
        <FloatingActionBar
          role="operator"
          onCancel={handleCancel}
          onSubmit={() => form.submit()}
          loading={saving}
          submitText={t('common.button.save')}
        />
      )}

      <Card
        title={t('shared.warehouses.zones.title')}
        style={{ marginTop: 24 }}
        extra={
          canEditZones ? (
            <Button
              type="primary"
              icon={<PlusOutlined />}
              onClick={handleZoneAdd}
            >
              {t('shared.warehouses.zones.add')}
            </Button>
          ) : (
            <Tooltip title={t('shared.warehouses.zones.switch_current_warehouse_to_edit')}>
              <Button type="primary" icon={<PlusOutlined />} disabled>
                {t('shared.warehouses.zones.add')}
              </Button>
            </Tooltip>
          )
        }
      >
        <Table
          dataSource={zones}
          columns={zoneColumns}
          rowKey="id"
          loading={zonesLoading}
          pagination={false}
          size="small"
          locale={{ emptyText: t('shared.warehouses.zones.no_zones') }}
        />
      </Card>

      <Modal
        title={editingZone ? t('shared.warehouses.zones.edit') : t('shared.warehouses.zones.add')}
        open={zoneModalVisible}
        onOk={handleZoneModalOk}
        onCancel={() => setZoneModalVisible(false)}
        confirmLoading={zoneModalLoading}
        okText={t('common.button.confirm')}
        cancelText={t('common.button.cancel')}
        destroyOnClose
      >
        <Form form={zoneForm} layout="vertical">
          <Form.Item
            name="name"
            label={t('shared.warehouses.zones.name_label')}
            rules={[{ required: true, message: t('common.validation.required') }]}
          >
            <Input placeholder={t('shared.warehouses.zones.name_placeholder')} />
          </Form.Item>
        </Form>
      </Modal>

      <Modal
        title={t('shared.warehouses.zones.delete_confirm')}
        open={deleteZoneModal.visible}
        onOk={handleDeleteZoneConfirm}
        onCancel={() => setDeleteZoneModal({ visible: false, zone: null, shelvesCount: 0 })}
        confirmLoading={deleteZoneLoading}
        okText={t('common.button.confirm')}
        okButtonProps={{ danger: true }}
        cancelText={t('common.button.cancel')}
      >
        <Typography.Paragraph>
          {t('shared.warehouses.zones.delete_has_shelves', { count: deleteZoneModal.shelvesCount })}
        </Typography.Paragraph>
        <Radio.Group
          value={deleteAction}
          onChange={(e) => {
            setDeleteAction(e.target.value);
            if (e.target.value === 'clear') setReplacementZoneId(null);
          }}
          style={{ width: '100%' }}
        >
          <Space direction="vertical" style={{ width: '100%' }}>
            <Radio value="replace">
              {t('shared.warehouses.zones.delete_option_replace')}
            </Radio>
            {deleteAction === 'replace' && (
              <Select
                style={{ width: '100%', marginLeft: 24 }}
                placeholder={t('shared.warehouses.zones.select_replacement_zone')}
                value={replacementZoneId}
                onChange={setReplacementZoneId}
                options={zones
                  .filter((z) => z.id !== deleteZoneModal.zone?.id)
                  .map((z) => ({ value: z.id, label: z.name }))}
              />
            )}
            <Radio value="clear">
              {t('shared.warehouses.zones.delete_option_clear')}
            </Radio>
          </Space>
        </Radio.Group>
      </Modal>
    </div>
  );
};

export default WarehouseShow;
